import { useSyncExternalStore } from 'react';
import { fromLegacyHash, parseLocation, toPath } from './routes';
import type { Route } from './routes';

/** pushState and replaceState fire nothing; this tells the route's readers. */
const NAVIGATED = 'fleet:navigate';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener(NAVIGATED, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(NAVIGATED, onChange);
  };
}

const readLocation = (): string => window.location.pathname + window.location.search;

/** The current route, re-read whenever the address changes — from a click or the back button. */
export function useRoute(): Route {
  const at = useSyncExternalStore(subscribe, readLocation);
  const query = at.indexOf('?');
  return query < 0 ? parseLocation(at) : parseLocation(at.slice(0, query), at.slice(query));
}

/** Go somewhere. It is a history entry, so Back returns to the previous screen. */
export function navigate(route: Route): void {
  const path = toPath(route);
  if (readLocation() === path) return;
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event(NAVIGATED));
}

/**
 * Change the address without a history entry — a new conversation getting its
 * id, or a dead link corrected — so Back does not return to what was replaced.
 */
export function replaceRoute(route: Route): void {
  const path = toPath(route);
  if (readLocation() === path) return;
  window.history.replaceState(window.history.state, '', path);
  window.dispatchEvent(new Event(NAVIGATED));
}

/** Once, before the first render: an old `#/…` link lands where it meant to. */
export function upgradeLegacyHash(): void {
  const path = fromLegacyHash(window.location.hash);
  if (path !== undefined) window.history.replaceState(null, '', path);
}
